import { useTranslation } from 'react-i18next'

import Icon from '../../ui/Icon'
import { Button } from '../../buttons/Button'
import { InputTime } from '../inputTime/InputTime'

import { TimeInterval } from '../../../types/booking/booking'
import { toTimeValue } from '../../../utils/dateUtils'

type IntervalListInputProps = {
  intervals: TimeInterval[]
  disabled?: boolean
  onChange: (intervals: TimeInterval[]) => void
}

export const isValidInterval = (interval: TimeInterval) => {
  const start = toTimeValue(interval.startTime)
  const end = toTimeValue(interval.endTime)
  // 00:00 as end means midnight (end of day).
  return Boolean(start) && Boolean(end) && (end === '00:00' || start < end)
}

/** Daily playing intervals (a pass may allow several, e.g. mornings and late evenings). */
export const IntervalListInput = ({ intervals, disabled = false, onChange }: IntervalListInputProps) => {
  const { t } = useTranslation('common')

  const update = (index: number, field: keyof TimeInterval, value: string) =>
    onChange(intervals.map((interval, i) => (i === index ? { ...interval, [field]: value } : interval)))

  return (
    <div className="interval-list">
      {intervals.map((interval, index) => (
        <div className="row g-2 align-items-end mb-2" key={index}>
          <div className="col-5">
            <InputTime
              name={`start-${index}`}
              label={t('interval-input.from')}
              value={interval.startTime}
              disabled={disabled}
              onChange={(_, value) => update(index, 'startTime', value)}
              required
            />
          </div>
          <div className="col-5">
            <InputTime
              name={`end-${index}`}
              label={t('interval-input.to')}
              value={interval.endTime}
              disabled={disabled}
              onChange={(_, value) => update(index, 'endTime', value)}
              errorMessage={isValidInterval(interval) ? undefined : t('interval-input.invalid')}
              required
            />
          </div>
          <div className="col-2">
            <Button
              label={t('interval-input.remove')}
              icon={<Icon name="trash" size={14} />}
              iconOnly
              variant="ghost"
              disabled={disabled || intervals.length <= 1}
              onClick={() => onChange(intervals.filter((_, i) => i !== index))}
            />
          </div>
        </div>
      ))}
      <Button
        label={t('interval-input.add')}
        icon={<Icon name="plus" size={14} />}
        variant="ghost"
        size="sm"
        disabled={disabled}
        onClick={() => onChange([...intervals, { startTime: '08:00', endTime: '12:00' }])}
      />
    </div>
  )
}
